import { useEffect, useState } from 'react';

import { head, isEmpty } from 'lodash';

import { useDispatch } from 'react-redux';

import { notify, redirectTo } from '../../../shared/reducers/page';
import { useAppSelector } from '../../../shared/store';
import {
  addProducts,
  submitToCheckout,
  submitToCheckoutKurlyPass,
} from '../../../cart/shared/reducers/cart.slice';
import { selectCurrentTotalPrice, selectedDealProducts } from '../selectors';

import useSearchLocation from './useSearchLocation';

import { isPC } from '../../../../util/window/getDevice';

import { CART_PATH, COMMON_PATH, getPageUrl } from '../../../shared/constant';

import { DealProduct } from '../types';
import { DeliveryTimeType } from '../../../shared/interfaces';

import Alert from '../../../shared/components/Alert/Alert';
import {
  getCourierOperationPolicy,
} from '../../../shared/utils/getCourierOperationPolicy';
import { ProductDetailState } from '../slice';
import {
  AddToCartProduct,
} from '../../../shared/amplitude/events/product/AddToCartProduct';
import {
  AddToCartSuccess,
} from '../../../shared/amplitude/events/product/AddToCartSuccess';
import { amplitudeService } from '../../../shared/amplitude';
import {
  storeCartCounter,
} from '../../../cart/shared/services/cart-counter.storage.service';
import {
  AddToCartFail,
} from '../../../shared/amplitude/events/product/AddToCartFail';
import { branchService } from '../../../shared/branch';
import { AddToCart } from '../../../shared/branch/events';
import { getFusionQueryId } from '../shared/utils/productDetailEvent';

interface EventAddToCartProductProps {
  productDetailState: ProductDetailState;
  defaultContentId: number;
  selectedProducts: DealProduct[];
  totalPrice: number;
  totalBasePrice: number;
  totalRetailPrice: number;
  fusionQueryId: string | null;
}

const eventAddToCartProduct = async ({
  productDetailState,
  defaultContentId,
  selectedProducts,
  totalPrice,
  totalBasePrice,
  totalRetailPrice,
  fusionQueryId,
}: EventAddToCartProductProps) => {
  selectedProducts.forEach((it) => {
    branchService.logEvent(
      new AddToCart( [{
        $canonical_identifier: `product/${productDetailState.no}`,
        $sku: productDetailState.no,
        $price: it.discountedPrice || it.basePrice,
        $quantity: it.quantity,
        $currency: 'KRW',
        $product_name: productDetailState.name,
      }]),
    );
    amplitudeService
      .logEvent(new AddToCartProduct({
        product: productDetailState,
        defaultContentId: defaultContentId,
        dealProduct: it,
        totalPrice,
        totalBasePrice,
        totalRetailPrice,
        fusionQueryId,
      }));
  });
};

interface Props {
  isSoldOut: boolean;
  isDirectOrder: boolean;
  isPurchaseStatus: boolean;
}

export default function useProductBuy({
  isSoldOut,
  isDirectOrder,
  isPurchaseStatus,
}: Props) {
  const { isGuest } = useAppSelector(({ auth }) => auth);
  const { queryId } = useAppSelector(({ productList }) => productList);
  const productDetailState = useAppSelector(({ productDetail }) => productDetail);
  const { currentAddress } = useAppSelector(({ shippingAddress }) => shippingAddress);
  const selectedProducts = useAppSelector(selectedDealProducts);
  const totalPrice = useAppSelector(selectCurrentTotalPrice);

  const totalBasePrice = selectedProducts.reduce((pre, curr) => pre + curr.quantity * curr.basePrice, 0);
  const totalRetailPrice = selectedProducts.reduce((pre, curr) => pre + curr.quantity * (curr.retailPrice ?? 0), 0);
  const selectedDealTotalCount = selectedProducts.reduce((pre, curr) => pre + curr.quantity, 0);

  const  {
    defaultContentId, name, maxEa, minEa, mainImageUrl, exceptionLabel,
  } = productDetailState;

  const [buyText, setBuyText] = useState('장바구니 담기');

  const { searchUserLocation } = useSearchLocation();

  const dispatch = useDispatch();

  const isKurlyPass = exceptionLabel === 'KURLY_PASS';

  useEffect(() => {
    if (isPC && (isSoldOut || !isPurchaseStatus)) {
      setBuyText('상품준비중입니다');
      return;
    }

    if (isDirectOrder) {
      setBuyText('바로구매');
      return;
    }

    setBuyText('장바구니 담기');
  }, [isSoldOut, isPurchaseStatus, isDirectOrder]);

  const getInvalidQuantityMessage = (): string | null => {
    const totalQuantity = selectedProducts.reduce((prev, curr) => prev + curr.quantity, 0);

    if (minEa > totalQuantity) {
      return `${name}상품의 최소 구매 수량은 ${minEa}개입니다.`;
    }

    if (maxEa < totalQuantity) {
      return `${name}상품의 최대 구매 수량은 ${maxEa}개입니다.`;
    }

    return null;
  };

  // 장바구니 담기
  const addToCart = async () => {
    const items = selectedProducts.map((it) => ({
      dealProductNo: it.no,
      quantity: it.quantity,
    }));

    if (isEmpty(items)) {
      throw Error('주문하실 상품을 선택해주세요.');
    }

    const invalidQuantityMessage = getInvalidQuantityMessage();

    if (invalidQuantityMessage) {
      await amplitudeService.logEvent(new AddToCartFail({
        fusionQueryId: getFusionQueryId(queryId),
      }));

      throw Error(invalidQuantityMessage);
    }

    if (!head(selectedProducts)) {
      throw Error('주문하실 상품을 선택해주세요.');
    }

    const panelOption = {
      representativeItem: {
        imageUrl: mainImageUrl,
        name: selectedProducts[0].name,
      },
      isOpenPanel: true,
    };

    try {
      dispatch(addProducts(items, panelOption));

      const fusionQueryId = getFusionQueryId(queryId);

      await eventAddToCartProduct({
        productDetailState,
        defaultContentId,
        selectedProducts,
        totalPrice,
        totalBasePrice,
        totalRetailPrice,
        fusionQueryId,
      });
      await amplitudeService.logEvent(new AddToCartSuccess({
        productDetailState,
        defaultContentId,
        totalPrice,
        totalBasePrice,
        totalRetailPrice,
        selectedDealTotalCount,
        fusionQueryId,
      }));
    } catch (err) {
      await amplitudeService.logEvent(new AddToCartFail({
        fusionQueryId: getFusionQueryId(queryId),
      }));

      dispatch(notify(err.message));
    }
  };

  // 바로구매
  const addToCheckout = async () => {
    const targetProducts = selectedProducts.map((it) => ({
      dealProductNo: it.no,
      quantity: it.quantity,
    }));

    if (isEmpty(targetProducts)) {
      Alert({
        title: '알림',
        text: '주문하실 상품을 선택해주세요.',
      });
      return;
    }

    if (isGuest && isKurlyPass) {
      const returnUrl = encodeURIComponent(window.location.href);

      Alert({
        title: '알림',
        text: '비회원은 컬리패스 서비스를 이용하실 수 없습니다.',
        handleClickConfirmButton: () => dispatch(redirectTo({ url: `${getPageUrl(COMMON_PATH.login)}?guest=1&return_url=${returnUrl}` })),
      });
      return;
    }

    // 현재 배송 정책을 확인한다.
    const actualDeliveryType = getCourierOperationPolicy({
      deliveryType: currentAddress?.deliveryType ?? 'direct',
      policies: currentAddress?.policies ?? [],
    });

    const params = {
      dealProducts: targetProducts,
      address: currentAddress?.roadAddress ?? '',
      addressDetail: currentAddress?.addressDetail,
      deliveryPolicy: actualDeliveryType === 'direct'
        ? DeliveryTimeType.DAWN
        : DeliveryTimeType.DAY,
      isDirectCheckout: true,
    };

    if (isGuest) {
      const returnUrl = encodeURIComponent(CART_PATH.counter.uri);

      storeCartCounter(params);
      dispatch(redirectTo({ url: `${getPageUrl(COMMON_PATH.login)}?guest=1&return_url=${returnUrl}` }));
      return;
    }

    const hasAddress = !!currentAddress?.address;

    if (!hasAddress) {
      searchUserLocation();
      return;
    }

    if (isKurlyPass) {
      dispatch(submitToCheckoutKurlyPass());
      return;
    }

    dispatch(submitToCheckout(params));

    const fusionQueryId = getFusionQueryId(queryId);

    await eventAddToCartProduct({
      productDetailState,
      defaultContentId,
      selectedProducts,
      totalPrice,
      totalBasePrice,
      totalRetailPrice,
      fusionQueryId,
    });
    await amplitudeService.logEvent(new AddToCartSuccess({
      productDetailState,
      defaultContentId,
      totalPrice,
      totalBasePrice,
      totalRetailPrice,
      selectedDealTotalCount,
      fusionQueryId,
    }));
  };

  const buyProduct = async () => {
    if (isSoldOut) {
      return;
    }

    if (isDirectOrder) {
      await addToCheckout();
      return;
    }

    await addToCart();
  };

  return { buyText, buyProduct };
}
